import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, GripVertical, MoveVertical, Save, X, Users } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';
import ImageUploader from '../../components/ImageUploader';

const EMPTY = { name: '', role: '', photo_url: '' };

export default function AdminBoardPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/board-members');
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const dnd = useReorder(items, (it) => it.id);
  const visible = dnd.reorderMode ? dnd.orderDraft.map((id) => items.find((i) => i.id === id)).filter(Boolean) : items;

  async function persistOrder(ids) {
    try {
      await api.put('/admin/board-members/reorder', { ids });
      await load();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  function startEdit(it) {
    setEditingId(it.id);
    setForm({ name: it.name || '', role: it.role || '', photo_url: it.photo_url || '' });
    setShowForm(true); setError('');
  }
  function startCreate() {
    setEditingId(null); setForm(EMPTY); setShowForm(true); setError('');
  }
  function cancelForm() {
    setShowForm(false); setEditingId(null); setForm(EMPTY); setError('');
  }

  async function save() {
    setSaving(true); setError('');
    try {
      if (editingId) await api.put(`/admin/board-members/${editingId}`, form);
      else await api.post('/admin/board-members', form);
      await load();
      cancelForm();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar este miembro del consejo?')) return;
    try { await api.delete(`/admin/board-members/${id}`); await load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-board-page" className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Users size={22} />Consejo Directivo</h1>
          <p className="text-charcoal-500 text-sm mt-1">{items.length} miembros · Arrastra para reordenar</p>
        </div>
        <div className="flex items-center gap-2">
          {dnd.reorderMode ? (
            <>
              <button onClick={() => dnd.saveOrder(persistOrder)} disabled={dnd.saving} data-testid="board-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-emerald-700">
                <Save size={14} />Guardar orden
              </button>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
                <X size={14} />Cancelar
              </button>
            </>
          ) : (
            <>
              <button onClick={dnd.enterReorder} data-testid="board-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
                <MoveVertical size={14} />Reordenar
              </button>
              <button onClick={startCreate} data-testid="board-create-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-cmic-700">
                <Plus size={14} />Añadir
              </button>
            </>
          )}
        </div>
      </header>

      {dnd.reorderMode && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">Arrastra los miembros con el icono ⋮⋮ para reordenarlos y haz click en "Guardar orden".</div>}

      {showForm && (
        <div className="bg-white border border-cmic-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="board-form">
          <h3 className="font-semibold text-charcoal-800">{editingId ? 'Editar miembro' : 'Nuevo miembro del consejo'}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre completo</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="board-form-name" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Cargo / Rol</label>
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="board-form-role" placeholder="Ej: Presidente, Tesorera..." className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Foto</label>
            <ImageUploader value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} testid="board-form-photo" shape="circle" />
          </div>
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2.5">{error}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving || !form.name.trim() || !form.role.trim()} data-testid="board-form-save" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
              <Save size={14} />{saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button onClick={cancelForm} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-charcoal-500">
          <Users size={32} className="mx-auto mb-3 text-charcoal-300" />
          No hay miembros aún. Añade el primero.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((it) => (
            <li key={it.id} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(it.id) : {})} data-testid={`board-row-${it.id}`} className={`bg-white rounded-2xl border p-5 flex items-start gap-3 ${dnd.reorderMode ? 'cursor-move border-amber-200 bg-amber-50/50' : 'border-gray-200 hover:border-cmic-300 hover:shadow-sm transition-all'}`}>
              {dnd.reorderMode && <GripVertical size={16} className="text-charcoal-400 mt-1 flex-shrink-0" />}
              {it.photo_url ? (
                <img src={it.photo_url} alt={it.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-cmic-100 flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-16 h-16 rounded-full bg-cmic-100 flex items-center justify-center text-cmic-600 font-bold text-lg flex-shrink-0">{(it.name || '?').slice(0, 1).toUpperCase()}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-charcoal-800 truncate">{it.name}</div>
                <div className="text-xs text-cmic-600 font-medium uppercase tracking-wider mt-0.5">{it.role}</div>
              </div>
              {!dnd.reorderMode && (
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(it)} title="Editar" data-testid={`board-edit-${it.id}`} className="p-1.5 rounded text-charcoal-500 hover:bg-charcoal-50"><Edit3 size={14} /></button>
                  <button onClick={() => remove(it.id)} title="Eliminar" data-testid={`board-delete-${it.id}`} className="p-1.5 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
